// HomeScreen (§4) — the mode hub: daily-reward card, codex progress ring, a
// "continue playing" Free Forge card, the game-modes grid, a forum entry, and a
// Recent Discoveries rail. Replaces StartScreen.tsx.
import { AD_SLOTS } from "../../config/adSlots";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Screen from "../ui/Screen";
import ElementOrb from "../ui/ElementOrb";
import { useElementStore } from "../../store/elementStore";
import { levelFromDiscovered, getStreak, claimDaily } from "../../game/stats";
import { playSound } from "../../services/soundService";
import { triggerHaptic } from "../../services/capacitorService";
import { ELEMENT_DEFS } from "../elementDefinitions";

const RING_R = 32;
const RING_C = 2 * Math.PI * RING_R;

const modeCard = (
  bg: string,
  border: string,
  iconBg: string,
  iconColor: string,
  icon: React.ReactNode,
  title: string,
  subtitle: string,
  subtitleColor: string,
  onClick: () => void
) => (
  <button
    className="mc-tap"
    onClick={onClick}
    style={{ textAlign: "left", padding: 16, borderRadius: 18, background: bg, border: `1px solid ${border}`, cursor: "pointer", color: "inherit" }}
  >
    <div style={{ width: 42, height: 42, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", background: iconBg, color: iconColor }}>{icon}</div>
    <div style={{ marginTop: 12, fontFamily: "'Space Grotesk',sans-serif", fontSize: 15, fontWeight: 700 }}>{title}</div>
    <div style={{ marginTop: 2, fontSize: 12, color: subtitleColor }}>{subtitle}</div>
  </button>
);

const HomeScreen: React.FC = () => {
  const navigate = useNavigate();
  const unlocked = useElementStore((s) => s.unlocked);
  const total = Object.keys(ELEMENT_DEFS).length;
  const discovered = unlocked.size;
  const level = levelFromDiscovered(discovered);
  const pct = Math.round((discovered / total) * 100);

  const [streakState, setStreakState] = useState(() => getStreak());
  const claimed = streakState.claimedToday;

  const recent = useMemo(() => {
    const ids = Array.from(unlocked).filter((id) => ELEMENT_DEFS[id] && ELEMENT_DEFS[id].tier > 1);
    return ids.slice(-8).reverse();
  }, [unlocked]);

  const goMode = (mode: string, path = "/play") => {
    triggerHaptic("light");
    navigate(path, { state: { mode } });
  };

  const handleClaim = () => {
    if (claimed) return;
    if (claimDaily()) {
      triggerHaptic("success");
      playSound("coin");
      setStreakState(getStreak());
    }
  };

  return (
    <Screen title="MystiCrafter" active="home" streak={streakState.streak} level={level} ad={AD_SLOTS.home}>
      <div className="mc-in" style={{ padding: "18px 16px 26px", display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Daily reward + Codex ring */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 14 }}>
          <div style={{ position: "relative", overflow: "hidden", padding: 18, borderRadius: 20, background: "linear-gradient(135deg,var(--hero-1),var(--hero-2))", border: "1px solid rgba(245,183,64,0.25)" }}>
            <div style={{ position: "absolute", right: -30, top: -30, width: 120, height: 120, borderRadius: "50%", background: "radial-gradient(circle,rgba(245,183,64,0.35),transparent 70%)" }} />
            <div style={{ position: "relative" }}>
              <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", color: "#F5B740" }}>Daily Reward</div>
              <div style={{ marginTop: 4, fontFamily: "'Space Grotesk',sans-serif", fontSize: 20, fontWeight: 700 }}>{streakState.streak}-day streak</div>
              <div style={{ marginTop: 3, fontSize: 13, color: "var(--text-2)" }}>Keep it alive for bonus crystals</div>
              <button
                className="mc-tap"
                onClick={handleClaim}
                disabled={claimed}
                style={{
                  marginTop: 12,
                  minHeight: 40,
                  padding: "0 16px",
                  border: "none",
                  borderRadius: 11,
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: claimed ? "default" : "pointer",
                  background: claimed ? "var(--fill)" : "#F5B740",
                  color: claimed ? "var(--text-3)" : "#231A05",
                }}
              >
                {claimed ? "Claimed today ✓" : "Claim reward"}
              </button>
            </div>
          </div>

          <div style={{ padding: 18, borderRadius: 20, background: "var(--surface)", border: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ position: "relative", width: 74, height: 74, flex: "0 0 auto" }}>
              <svg width="74" height="74" viewBox="0 0 74 74" style={{ transform: "rotate(-90deg)" }}>
                <circle cx="37" cy="37" r={RING_R} fill="none" stroke="var(--fill)" strokeWidth="7" />
                <circle cx="37" cy="37" r={RING_R} fill="none" stroke="#65A30D" strokeWidth="7" strokeLinecap="round" strokeDasharray={`${(discovered / total) * RING_C} ${RING_C}`} />
              </svg>
              <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 18, fontWeight: 700 }}>{discovered}</span>
                <span style={{ fontSize: 10, color: "var(--text-3)" }}>/ {total}</span>
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 16, fontWeight: 700 }}>Your Codex</div>
              <div style={{ marginTop: 2, fontSize: 13, color: "var(--text-2)" }}>{pct}% of all elements discovered</div>
              <button className="mc-tap" onClick={() => navigate("/codex")} style={{ marginTop: 10, minHeight: 40, padding: "0 14px", border: "1px solid rgba(101,163,13,0.4)", borderRadius: 11, background: "rgba(101,163,13,0.12)", color: "var(--accent-text)", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                Open Codex
              </button>
            </div>
          </div>
        </div>

        {/* Continue playing — Free Forge */}
        <button className="mc-tap" onClick={() => navigate("/play")} style={{ textAlign: "left", position: "relative", overflow: "hidden", padding: 22, borderRadius: 22, border: "1px solid rgba(101,163,13,0.35)", background: "linear-gradient(135deg,var(--hero-1),var(--hero-2) 60%)", cursor: "pointer", color: "inherit" }}>
          <div style={{ position: "absolute", right: -20, bottom: -30, width: 180, height: 180, borderRadius: "50%", background: "radial-gradient(circle,rgba(101,163,13,0.4),transparent 70%)" }} />
          <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ width: 56, height: 56, flex: "0 0 auto", borderRadius: 16, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(101,163,13,0.2)", border: "1px solid rgba(101,163,13,0.4)" }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--accent-text)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M9 3h6M10 3v5.5L5 18a2 2 0 0 0 1.8 3h10.4A2 2 0 0 0 19 18l-5-9.5V3" /></svg>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", color: "var(--accent-text)" }}>Continue playing</div>
              <div style={{ marginTop: 3, fontFamily: "'Space Grotesk',sans-serif", fontSize: 22, fontWeight: 700 }}>Free Forge</div>
              <div style={{ marginTop: 2, fontSize: 13, color: "var(--text-2)" }}>{discovered} elements discovered · mix anything</div>
            </div>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--accent-text)" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" style={{ flex: "0 0 auto" }}><path d="M9 18l6-6-6-6" /></svg>
          </div>
        </button>

        {/* Game modes */}
        <div>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 10 }}>
            <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 16, fontWeight: 700 }}>Game Modes</span>
            <span style={{ fontSize: 12, color: "var(--text-3)" }}>Pick a challenge</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(150px,1fr))", gap: 12 }}>
            {modeCard("var(--surface)", "var(--border)", "rgba(96,165,250,0.14)", "#60A5FA",
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="13" r="8" /><path d="M12 9v4l2 2M9 2h6" /></svg>,
              "Time Attack", "Race the clock", "var(--text-2)", () => goMode("Time Attack"))}
            {modeCard("var(--surface)", "var(--border)", "rgba(245,183,64,0.14)", "#F5B740",
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="3" /><path d="M3 10h18M8 2v4M16 2v4" /></svg>,
              "Daily Puzzle", "New goal every day", "var(--text-2)", () => goMode("Daily Puzzle"))}
            {modeCard("var(--surface)", "var(--border)", "rgba(52,211,153,0.14)", "#34D399",
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M4 20h16M6 20V10M12 20V5M18 20v-7" /></svg>,
              "Gauntlet", "10 rising levels", "var(--text-2)", () => goMode("Gauntlet"))}
            {modeCard("linear-gradient(135deg,var(--hero-1),var(--hero-2))", "rgba(236,72,153,0.3)", "rgba(236,72,153,0.16)", "#F472B6",
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M14.5 17.5L3 6V3h3l11.5 11.5M13 19l6-6M16 16l4 4M19 21l2-2" /></svg>,
              "Battle Arena", "Live 1v1 races", "#F9A8D4", () => { triggerHaptic("light"); navigate("/battle"); })}
            {modeCard("var(--surface)", "var(--border)", "rgba(180,140,255,0.14)", "#B48CFF",
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M9 3h6M10 3v6l-4.5 8A2 2 0 0 0 7.3 20h9.4a2 2 0 0 0 1.8-3L14 9V3M7 15h10" /></svg>,
              "Combination Lab", "Multi-slot workbench", "var(--text-2)", () => goMode("Lab Mode"))}
          </div>
        </div>

        {/* Forum entry */}
        <button className="mc-tap" onClick={() => navigate("/forums")} style={{ textAlign: "left", display: "flex", alignItems: "center", gap: 14, padding: "16px 18px", borderRadius: 18, background: "var(--surface)", border: "1px solid var(--border)", cursor: "pointer", color: "inherit" }}>
          <div style={{ width: 44, height: 44, flex: "0 0 auto", borderRadius: 13, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(96,165,250,0.14)", color: "#60A5FA" }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.5 8.5 0 0 1-12.5 7.5L3 21l2-5.5A8.5 8.5 0 1 1 21 11.5z" /></svg>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 15, fontWeight: 700 }}>Community Forum</div>
            <div style={{ marginTop: 2, fontSize: 12, color: "var(--text-2)" }}>Share recipes, strategies & discoveries</div>
          </div>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
        </button>

        {/* Recent discoveries */}
        {recent.length > 0 && (
          <div>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 10 }}>
              <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 16, fontWeight: 700 }}>Recent Discoveries</span>
              <span className="mc-tap" onClick={() => navigate("/codex")} style={{ fontSize: 12, color: "var(--accent-text)", cursor: "pointer" }}>See all</span>
            </div>
            <div className="mc-scroll" style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 4 }}>
              {recent.map((id) => (
                <div key={id} style={{ flex: "0 0 auto", width: 76, display: "flex", flexDirection: "column", alignItems: "center", gap: 7 }}>
                  <ElementOrb id={id} size={54} withRing />
                  <span style={{ fontSize: 11, color: "var(--text-2)", textAlign: "center", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 76 }}>{ELEMENT_DEFS[id]?.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Screen>
  );
};

export default HomeScreen;
